import type { GeneralCallType } from '@/fastApiV2/generated'
import type { IconName } from '@/types/icon-names'
import type {
  AudioClip,
  Countries,
  Country,
  CountryCode,
  CountryMasks,
  OperatorData,
  ToastTypes,
  TranslateFunction,
  UserStatus,
} from '@/types/types'
import { LanguagesLocales, Operators, ServiceKey } from '@/types/types'

export const userStatuses: Record<UserStatus, UserStatus> = {
  identified: 'identified',
  unidentified: 'unidentified',
}

export const locations: Countries = {
  ru: 'ru',
  uk: 'uk',
  kz: 'kz',
  ua: 'ua',
  by: 'by',
}

export const useCurrencyData = (t: TranslateFunction): Record<string, string> => {
  return {
    UAH: t('generic.currency.UAH.short'),
    BYN: t('generic.currency.BYN.short'),
    RUB: t('generic.currency.RUB.short'),
    KZT: t('generic.currency.KZT.short'),
  }
}

export const countryMasks: CountryMasks = {
  RU: '+7 999 999-99-99',
  KZ: '+7 799 999-99-99',
  UA: '+380 99-999-99-99',
  BY: '+375 99-999-99-99',
}

export const countryMasksExtended: CountryMasks = {
  RU: '+7 (999) 999-99-99',
  KZ: '+7 (799) 999-99-99',
  UA: '+380 (99)-999-99-99',
  BY: '+375 (99)-999-99-99',
}

export const toastTypes: ToastTypes = {
  info: 'primary',
  success: 'warning',
  warning: 'error',
  normal: '',
}

export const monthsNum = [
  'generic.january',
  'generic.february',
  'generic.march',
  'generic.april',
  'generic.may',
  'generic.june',
  'generic.july',
  'generic.august',
  'generic.september',
  'generic.october',
  'generic.november',
  'generic.december',
]

export const useExpensesTypes = (t: TranslateFunction): Record<string, string> => ({
  out: t('generic.outgoing'),
  sms: t('generic.sms'),
  incoming: t('generic.inbox'),
  other: t('generic.another'),
})

export const supTypes: Record<string, string> = {
  cabinet: 'cabinet',
  api: 'api',
  incoming: 'incoming',
  confirmation: 'confirmation',
}

export const requestsDelays: Record<string, number> = {
  balance: 5000,
  techSupportMessagesCount: 10000,
  authNumberDate: 5000,
  campaignStatus: 5000,
  campaignStatistic: 10000,
}

export const useLanguages = (t: TranslateFunction): Record<LanguagesLocales, string> => ({
  [LanguagesLocales.RU]: t('generic.russian'),
  [LanguagesLocales.EN]: t('generic.english'),
  [LanguagesLocales.UA]: t('generic.ukrainian'),
  [LanguagesLocales.KZ]: t('generic.kazakh'),
  [LanguagesLocales.BY]: t('generic.belarusian'),
})

export const countryCodesLanguages = {
  RU: LanguagesLocales.RU,
  EN: LanguagesLocales.EN,
  UA: LanguagesLocales.UA,
  KZ: LanguagesLocales.KZ,
  BY: LanguagesLocales.BY,
}

export const countryCodes: Record<string, string> = {
  RU: 'RU',
  UA: 'UA',
  BY: 'BY',
  KZ: 'KZ',
}

export const useCountries = (t: TranslateFunction): Record<CountryCode, Country> => ({
  RU: {
    name: t('generic.russia.nominative'),
    icon: 'ru.png',
    englishName: 'Russia',
    locale: 'ru-ru',
    code: 'RU',
    currencySymbol: '₽',
  },
  UA: {
    name: t('generic.ukraine.nominative'),
    icon: 'ua.png',
    englishName: 'Ukraine',
    locale: 'uk-ua',
    code: 'UA',
    currencySymbol: '₴',
  },
  BY: {
    name: t('generic.belarus.nominative'),
    icon: 'by.png',
    englishName: 'Belarus',
    locale: 'ru-by',
    code: 'BY',
    currencySymbol: 'Br',
  },
  KZ: {
    name: t('generic.kazakhstan.nominative'),
    icon: 'kz.png',
    englishName: 'Kazakhstan',
    locale: 'ru-kz',
    code: 'KZ',
    currencySymbol: '₸',
  },
})

export const gtmAuthEventsSteps = {
  login: {
    step1: {
      // Зашли на стр авторизации
      mobile: '<avt_b_m1>',
      desktop: '<avt_b_d1>',
    },
    step2: {
      // Нажали на "Войти через старую версию"
      mobile: '<avt_b_m1_1>',
      desktop: '<avt_b_d1_1>',
    },
    step3: {
      // Успешная авторизация
      mobile: '<avt_b_m2>',
      desktop: '<avt_b_d2>',
    },
  },
  registration: {
    step1: {
      // Зашли на стр регистрации
      mobile: '<reg_b_m1>',
      desktop: '<reg_b_d1>',
    },
    step2: {
      // Зашли на вторую стр регистрации
      mobile: '<reg_b_m2>',
      desktop: '<reg_b_d2>',
    },
    step3: {
      // Нажали на "Создать новый аккаунт"
      mobile: '<reg_b_m2_1>',
      desktop: '<reg_b_d2_1>',
    },
    step4: {
      // Успешная регистрация
      mobile: '<reg_b_m3>',
      desktop: '<reg_b_d3>',
    },
  },
}

export const paymentTemplates: Record<CountryCode, number[]> = {
  RU: [5000, 10000, 30000, 50000],
  UA: [2000, 3000, 4000, 5000],
  BY: [100, 200, 300, 500],
  KZ: [5000, 10000, 20000, 30000],
}

export const useRecognitionLanguages = (t: TranslateFunction): Record<CountryCode, Record<string, string>> => ({
  RU: {
    languages: t('generic.russian'),
  },
  UA: {
    languages: `${t('generic.ukrainian')}, ${t('generic.russian')}`,
  },
  BY: {
    languages: t('generic.russian'),
  },
  KZ: {
    languages: `${t('generic.kazakh')}, ${t('generic.russian')}`,
  },
})

export const useAudioClipType = (t: TranslateFunction): Record<AudioClip, string> => ({
  synth: t('audio.synthesized_clip'),
  uploaded: t('audio.uploaded_file'),
  waiting: t('audio.voice_acting_by_announcer'),
  speaker: t('audio.voice_acting_by_announcer'),
  reworked: t('audio.recorded_by_the_announcer_revised'),
  upread: t('audio.finishing_reading_temporarily_recorded_by_synthesizer'),
  reread: t('audio.rereading_temporarily_recorded_by_synthesizer'),
  upreaded: t('audio.read_to_the_end_by_the_announcer'),
  rereaded: t('audio.reread'),
})

export const useAudioClipTypeCustom = (t: TranslateFunction): Record<AudioClip, string> => ({
  uploaded: t('audio.uploaded'),
  synth: t('audio.synthesized'),
  waiting: t('audio.waiting_to_be_recorded_temporarily_recorded_by_synthesizer'),
  speaker: t('audio.recorded_by_the_announcer'),
  reworked: t('audio.recorded_by_the_announcer_revised'),
  upread: t('audio.finishing_reading_temporarily_recorded_by_synthesizer'),
  reread: t('audio.rereading_temporarily_recorded_by_synthesizer'),
  upreaded: t('audio.read_to_the_end_by_the_announcer'),
  rereaded: t('audio.reread'),
})

export const operatorsData: Record<Operators, OperatorData> = {
  // Россия
  [Operators.MTT]: { icon: 'mtt', nameKey: 'phone_list.operators.mtt', country: 'RU' },
  [Operators.MTS]: { icon: 'mts', nameKey: 'phone_list.operators.mts', country: 'RU' },
  [Operators.TELE2]: { icon: 'tele2', nameKey: 'phone_list.operators.tele2', country: 'RU' },
  [Operators.MGF]: { icon: 'mgf', nameKey: 'phone_list.operators.mgf', country: 'RU' },
  [Operators.BLN]: { icon: 'beeline', nameKey: 'phone_list.operators.beeline', country: 'RU' },
  [Operators.TINKOFF]: { icon: 'tinkoff', nameKey: 'phone_list.operators.tinkoff', country: 'RU' },
  // Беларусь
  [Operators.VELCOM]: { icon: null, nameKey: 'phone_list.operators.velcom', country: 'BY' },
  [Operators.MTSBY]: { icon: null, nameKey: 'phone_list.operators.mtsby', country: 'BY' },
  [Operators.LIFEBY]: { icon: null, nameKey: 'phone_list.operators.lifeby', country: 'BY' },
  // Казахстан
  [Operators.KARTEL]: { icon: null, nameKey: 'phone_list.operators.kartel', country: 'KZ' },
  [Operators.KCELL]: { icon: null, nameKey: 'phone_list.operators.kcell', country: 'KZ' },
  [Operators.TELE2KZ]: { icon: null, nameKey: 'phone_list.operators.tele2kz', country: 'KZ' },
  // Украина
  [Operators.VODAFONE]: { icon: null, nameKey: 'phone_list.operators.vodafone', country: 'UA' },
  [Operators.KYIVSTAR]: { icon: null, nameKey: 'phone_list.operators.uakyiv', country: 'UA' },
  [Operators.LIFECELL]: { icon: null, nameKey: 'phone_list.operators.lifecell', country: 'UA' },
}

// Список операторов конкретной страны
export const getOperatorsByCountry = (country: CountryCode): Operators[] =>
  (Object.keys(operatorsData) as Operators[]).filter(operator => operatorsData[operator].country === country)

// ВНИМАНИЕ: значения намеренно заданы строковыми литералами, а GeneralCallType
// импортируется как `import type` (см. верх файла). НЕ заменяйте литералы на
// GeneralCallType.OUTGOING/INCOMING/FORWARDING — это превратит импорт в рантайм-import,
// который через этот шаренный модуль затянет весь SDK @/fastApiV2/generated (с
// обращением к GLOBALS при инициализации) в бандл виджета жалоб (widgets/complaints,
// внешнее встраивание без GLOBALS) и снова его сломает. См. ZVNK-4340.
export const useGeneralCallTypeOptions = (t: TranslateFunction): { value: GeneralCallType; label: string }[] => [
  {
    value: 'outgoing',
    label: t('generic.outgoing'),
  },
  {
    value: 'incoming',
    label: t('generic.inbox'),
  },
  {
    value: 'forwarding',
    label: t('generic.forwarding'),
  },
]

export const SERVICE_ICON_MAP: Record<ServiceKey, IconName> = {
  [ServiceKey.Synth]: 'voice',
  [ServiceKey.SynthPremium]: 'voice',
  [ServiceKey.CallConnectionPrior]: 'lightning',
  [ServiceKey.Transcribing]: 'transcription',
  [ServiceKey.TranscribingPremium]: 'transcription',
  [ServiceKey.TranscribingInhouse]: 'transcription',
  [ServiceKey.FasProtection]: 'large-icon-fas',
  [ServiceKey.Label]: 'info-border',
  [ServiceKey.AiDetectAa]: 'robot',
  [ServiceKey.NoIdent]: 'large-icon-incognito',
  [ServiceKey.BaseCaller]: 'phone-incoming',
  [ServiceKey.PremiumCaller]: 'phone-incoming',
  [ServiceKey.TrashCallDetector]: 'search',
}
